import Authenticating from '@/features/shared/Authenticating';
import { useMounted } from '@/hooks/useMounted';
import { Backdrop, Fade, Modal } from '@material-ui/core';
import React, { createContext, PropsWithChildren, useCallback, useContext } from 'react';
import { useExperlogixLoader } from './hooks/useExperlogixLoader';
import { usePowerAppsLoader } from './hooks/usePowerAppsLoader';
import { PowerAppsConnectProps } from './types';
import { usePowerAppsQueryParams } from './usePowerAppsQueryParams';

export const PowerAppsContext = createContext({
  powerAppsConnect: (connectProps?: PowerAppsConnectProps) => Promise.resolve(),
  powerAppsRefreshSession: () => Promise.resolve(),
  experlogixConnect: () => Promise.resolve(),
  isPowerAppsConnected: false,
  isPowerAppsConnecting: false,
  isPowerAppsSessionExpired: false,
  experlogixRefreshSession: () => Promise.resolve(),
  isExperlogixConnected: false,
  isExperlogixConnecting: false,
  experlogixIframeFailed: false,
  retryExperlogixWithPopup: () => Promise.resolve(),
  isExperlogixSessionExpired: false,
  disconnect: () => Promise.resolve(),
  persistedQueryParams: {
    queryParams: '',
    setQueryParams: ({ pathname = '', search = '' }) => {},
  },
});

export const PowerAppsProvider = ({ children }: PropsWithChildren<any>) => {
  const isMounted = useMounted();
  const persistedQueryParams = usePowerAppsQueryParams();

  const {
    load: loadPowerApps,
    refreshSession: powerAppsRefreshSession,
    isLoaded: isPowerAppsLoaded,
    isLoading: isPowerAppsLoading,
    isSessionExpired: isPowerAppsSessionExpired,
    unload: unloadPowerApps,
    useGlobalLoader: usePowerAppsGlobalLoader,
  } = usePowerAppsLoader();
  const {
    load: loadExperlogix,
    refresh: experlogixRefreshSession,
    isLoaded: isExperlogixLoaded,
    isLoading: isExperlogixLoading,
    iframeFailed: experlogixIframeFailed,
    isSessionExpired: isExperlogixSessionExpired,
    retryWithPopup: retryExperlogixWithPopup,
    unload: unloadExperlogix,
  } = useExperlogixLoader();

  const handleDisconnect = useCallback(async () => {
    await unloadExperlogix();
    await unloadPowerApps();
    return Promise.resolve();
  }, [unloadPowerApps, unloadExperlogix]);

  return (
    <PowerAppsContext.Provider
      value={{
        powerAppsConnect: loadPowerApps,
        powerAppsRefreshSession,
        experlogixConnect: loadExperlogix,
        isPowerAppsConnected: isPowerAppsLoaded,
        isPowerAppsConnecting: isPowerAppsLoading,
        isPowerAppsSessionExpired,
        experlogixRefreshSession,
        isExperlogixConnected: isExperlogixLoaded,
        isExperlogixConnecting: isExperlogixLoading,
        experlogixIframeFailed,
        isExperlogixSessionExpired,
        retryExperlogixWithPopup,
        disconnect: handleDisconnect,
        persistedQueryParams,
      }}
    >
      <Modal
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
        open={isPowerAppsLoading && usePowerAppsGlobalLoader}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={isPowerAppsLoading && usePowerAppsGlobalLoader}>
          <Authenticating />
        </Fade>
      </Modal>
      {isMounted && children}
    </PowerAppsContext.Provider>
  );
};

export const PreviewPowerAppsProvider = ({ children }: PropsWithChildren<any>) => (
  <PowerAppsContext.Provider
    value={{
      experlogixConnect: () => Promise.resolve(),
      powerAppsRefreshSession: () => Promise.resolve(),
      powerAppsConnect: () => Promise.resolve(),
      isPowerAppsConnected: false,
      isPowerAppsConnecting: false,
      isPowerAppsSessionExpired: false,
      experlogixRefreshSession: () => Promise.resolve(),
      isExperlogixConnected: false,
      isExperlogixConnecting: false,
      experlogixIframeFailed: false,
      isExperlogixSessionExpired: false,
      retryExperlogixWithPopup: () => Promise.resolve(),
      disconnect: () => Promise.resolve(),
      persistedQueryParams: {
        queryParams: '',
        setQueryParams: ({ pathname = '', search = '' }) => {},
      },
    }}
  >
    {children}
  </PowerAppsContext.Provider>
);

export const usePowerApps = () => {
  const powerApps = useContext(PowerAppsContext);

  if (!powerApps) {
    throw new Error('You need the PowerAppsProvider');
  }

  return powerApps;
};
